import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { AppConfig } from '../../shared/config/app-config';
import { LocalStorageService } from './local-storage.service';
import { DeviceRegistrationOtpModel } from '../../shared/models/device.model';
import {
	EmploymentDetails,
	Profile,
	ProfilePinResponse,
	ProfileRequestModel,
	UpdateAgentProfileRequest
} from '../../shared/models/profile.model';
import { Subject } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { TimelineModel } from '../../shared/models/timeline.model';

@Injectable({
	providedIn: 'root'
})
export class ProfileService {

	private _destroyComponent = new Subject<any>();
	_destroyComponent$ = this._destroyComponent.asObservable();

	private _destroyModalPin = new Subject<any>();
	_destroyModalPin$ = this._destroyModalPin.asObservable();

	private _updateComponent = new Subject<any>();
	_updateComponent$ = this._updateComponent.asObservable();

	private profile: Profile;
	private profileCaller;
	private isProfilePin;
	private displayAccountBalance;
	private stepArray: TimelineModel[];

	constructor(
		private apiClientHelper: ApiClientHelper,
		public config: AppConfig,
		private localStorage: LocalStorageService,
		private authService: AuthenticationService
	) {
	}

	setStepArray(arr: TimelineModel[]) {
		this.stepArray = [...arr];
	}

	getStepArray() {
		return this.stepArray;
	}

	setIsProfilePin(action) {
		this.isProfilePin = action;
	}

	destroyModalPin() {
		this._destroyModalPin.next(true);
	}

	setDisplayAccountBalance(display) {
		this.displayAccountBalance = display;
	}

	getDisplayAccountBalance() {
		return this.displayAccountBalance;
	}

	getIsProfilePin() {
		return this.isProfilePin;
	}

	destroyProfileComponent(value: any) {
		this._destroyComponent.next(value);
	}

	refreshComponent() {
		this._updateComponent.next(true);
	}

	setProfile(profile: Profile) {
		this.profile = profile;
	}

	getProfile() {
		return this.profile;
	}

	setProfileCaller(value) {
		this.profileCaller = value;
	}

	getProfileCaller() {
		return this.profileCaller;
	}

	getUserProfile(): Promise<ProfileRequestModel> {
		const context = this.getContext();
		let url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/`;
		switch (context.toLowerCase()) {
			case 'agents':
				url = url + `agents`;
				break;
			case 'agent-managers':
				url = url + `agent-managers/profile`;
				break;
			case 'aggregators':
			case 'area-manager':
				url = url + `aggregators`;
				break;
			case 'super-aggregator':
			case 'super-aggregators':
				url = url + `super-aggregators`;
				break;
		}

		return this.apiClientHelper.getForPromise(url);
	}

	initialResetPassword(param): Promise<DeviceRegistrationOtpModel> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profile/send-passphrase-update-otp`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	setPrintMerchantCopy(param): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/update-terminal-receipt-config`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	getReceiptQRCodeConfig(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/receipt-qr-configurations`;
		return this.apiClientHelper.getForPromise(url);
	}

	updateReceiptQRCodeConfig(param): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/receipt-qr-configurations`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	getDisplayBalanceStatus(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/display-wallet-balance`;
		return this.apiClientHelper.getForPromise(url);
	}

	updateDisplayBalanceStatus(param) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/update-balance-display`;
		return this.apiClientHelper.putForPromise(url, param);
	}

	setTerminalReprint(param): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/update/terminal-receipt-reprint`;
		return this.apiClientHelper.putForPromise(url, param);
	}

	setWithdrawalAuthorizationPinDecision(param): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/request-auth-pin-before-withdrawal`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	initialResetPin(params?): Promise<DeviceRegistrationOtpModel> {
		const context = this.getContext();
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/${context}/send-pin-reset-otp`;
		switch (context.toLowerCase()) {
			case 'agents':
			case 'aggregators':
			case 'area-manager':
			case 'super-aggregators':
				return this.apiClientHelper.getForPromise(url);
			case 'agent-managers':
				return this.apiClientHelper.postForPromise(url, params);
		}
	}

	resetPassword(param): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profile/passphrase`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	resetPin(param): Promise<ProfilePinResponse> {
		const context = this.getContext();
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/${context}/pin`;
		return this.apiClientHelper.postForPromise(url, param);
	}

	updateBank(param): Promise<any> {
		const context = this.getContext();
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/${context}/bank`;
		return this.apiClientHelper.putForPromiseI(url, param);
	}


	updateProfile(param: UpdateAgentProfileRequest): Promise<any> {
		const context = this.getContext();
		let url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/`;
		switch (context.toLowerCase()) {
			case 'agents':
				url = url + `agents`;
				return this.apiClientHelper.putForPromiseI(url, param);
			case 'agent-managers':
				url = url + `agent-managers/update`;
				break;
			case 'aggregators':
			case 'area-manager':
				url = url + `aggregators`;
				break;
			case 'super-aggregator':
			case 'super-aggregators':
				url = url + `super-aggregators`;
				break;
		}
		return this.apiClientHelper.postForPromise(url, param);

	}

	updateProfileWithPhotos(params) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	updateProfileWithMetaMap(params) {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/profiles/agents/`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	retrieveUsernameWithPhotos(params) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/forgot-username`;
		return this.apiClientHelper.noAuthPostAsFormDataForPromise(url, params);
	}

	valideUsernamePin(request): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/forgot-username/pin`;
		return this.apiClientHelper.postForPromise(url, request, false);
	}

	getContext() {
		return this.authService.getContextUser();
	}

	getRelationships() {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/next-of-kin/relationships`;
		return this.apiClientHelper.getForPromise(url);
	}

	getEmploymentDetails(): Promise<EmploymentDetails> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/employee-contract/aggregator`;
		return this.apiClientHelper.getForPromise(url);
	}

	updateNextOfKin(data, param): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/next-of-kin/${param}`;
		return this.apiClientHelper.putForPromiseI(url, data);
	}

	updateNextofKin(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/next-of-kin`;
		return this.apiClientHelper.putForPromiseI(url, params);
	}

	updateBalanceDisplay(request): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profile/update-balance-display`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	downLoadCertificate() {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/download-certificate`;
		return this.apiClientHelper.getAsBlobForPromise(url);
	}

	downloadAggregatorContract() {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/employee-contract/download-aggregator-contract`;
		return this.apiClientHelper.getAsBlobForPromise(url);
	}

	updateGeneralInformation(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/general-information`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	updateAddressInformation(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/address-information`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	updateIdentityDocuments(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/identification`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	updateAddressProof(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/address-proof`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	kycFaceValidation(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/face-match`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	getKycStatus(): Promise<KycStatus> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/customers/kycs/status`;
		return this.apiClientHelper.getForPromise(url);
	}

	getSelfTestStatus(): Promise<boolean> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/selftest-printing`;
		return this.apiClientHelper.getForPromise(url);
	}

	getEODPrintingStatus(): Promise<boolean> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/eod-printing`;
		return this.apiClientHelper.getForPromise(url);
	}

	updateSelfTestStatus(status): Promise<boolean> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/selftest-printing?enable=${status}`;
		return this.apiClientHelper.postForPromise(url, {});
	}

	updateEODPrintingStatus(status): Promise<boolean> {
		const url = this.config.MONIEPOINT_HOST + `/api/v2/businesses/eod-printing?enable=${status}`;
		return this.apiClientHelper.postForPromise(url, {});
	}
}

export interface KycStatus {
	providedGeneralInformation: boolean;
	providedNextOfKinInformation: boolean;
	providedFaceCaptureInformation: boolean;
	providedIdentificationInformation: boolean;
	providedAddressInformation: boolean;
	providedAddressProofInformation: boolean;
	addressVerificationStatus: any;
	identificationVerificationStatus: any;
	passportVerificationStatus: any;
	signatureVerificationStatus: any;
	utilityVerificationStatus: any;
	kycCompletionPercentage: number;
}
